import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Rocket, X } from "lucide-react";
import {
  supplierApi,
  type SupplierTarget,
  type SupplierTaskBrief,
  type TaskStatus,
} from "@/api/supplierClient";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ModelMappingEditor } from "@/components/ModelMappingEditor";
import { useTranslation } from "@/framework-originated/i18n";

// /supplier/distribute — 供应商自主「分发」(doc/051/doc/052):选主模板 → 录入 Key
// (单 / 多 / 批量)→ 一次下发到所选脱敏目标。主模板与绑定数据与运营自用一致(doc/052
// §2.2)。页面只调用 /supplier/*,响应只含 target_code;站点真实信息(名称/地址/ID/
// 端口/凭证)不进入本页任何请求或响应。

type Mode = "single" | "multi" | "batch";
type KeyFormat = "bearer" | "x-api-key" | "sigv4";
type PoolTarget = SupplierTarget;

const STATUS_VARIANT: Record<TaskStatus, "default" | "success" | "warning" | "destructive" | "muted"> = {
  staged: "muted",
  pending: "muted",
  dispatching: "warning",
  success: "success",
  failed: "destructive",
  unknown: "warning",
  abandoned: "muted",
};

const SIGV4_RE = /^[^\s|]+\|[^\s|]+\|[^\s|]+$/;

const KEY_FORMAT_PLACEHOLDER: Record<KeyFormat, { single: string; multi: string }> = {
  bearer: { single: "sk-ant-...", multi: "sk-ant-...\nsk-ant-..." },
  "x-api-key": { single: "sk-...", multi: "sk-...\nsk-..." },
  sigv4: { single: "AKIA...|SECRET...|us-east-1", multi: "AKIA...|SECRET...|us-east-1\nAKIA...|SECRET...|eu-west-1" },
};

const sectionCls = "rounded-xl border bg-card shadow-sm";
const inpCls = "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";
const labelCls = "text-xs font-medium text-foreground";
const hintCls = "text-[11px] text-muted-foreground";

function splitLines(text: string, dedupe: boolean): string[] {
  const lines = text.split("\n").map((s) => s.trim()).filter(Boolean);
  return dedupe ? Array.from(new Set(lines)) : lines;
}

function splitModels(csv: string): string[] {
  return Array.from(new Set(csv.split(",").map((m) => m.trim()).filter(Boolean)));
}

export function SupplierDistributePage() {
  const { t } = useTranslation("supplier");
  const qc = useQueryClient();

  const templatesQ = useQuery({
    queryKey: ["supplier", "templates"],
    queryFn: supplierApi.listTemplates,
    staleTime: 30_000,
  });
  const targetsQ = useQuery({
    queryKey: ["supplier", "targets"],
    queryFn: supplierApi.listTargets,
  });
  const recordsQ = useQuery({
    queryKey: ["supplier", "records", { page: 1, page_size: 8 }],
    queryFn: () => supplierApi.listRecords({ page: 1, page_size: 8 }),
  });

  const templates = templatesQ.data?.items ?? [];
  const allTargets = useMemo(() => targetsQ.data?.items ?? [], [targetsQ.data]);

  const [mode, setMode] = useState<Mode>("single");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [keyFormat, setKeyFormat] = useState<KeyFormat>("bearer");
  const [singleKey, setSingleKey] = useState("");
  const [multiText, setMultiText] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [models, setModels] = useState("");
  const [modelMapping, setModelMapping] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<SupplierTaskBrief[] | null>(null);
  const [duplicateKeys, setDuplicateKeys] = useState<Array<{ id: number; duplicateOf: number | null }>>([]);

  const activeTemplate = useMemo(
    () => templates.find((tpl) => tpl.id === templateId) ?? null,
    [templates, templateId],
  );

  const multiLines = useMemo(() => splitLines(multiText, true), [multiText]);
  const keyLines = mode === "single" ? (singleKey.trim() ? [singleKey.trim()] : []) : multiLines;
  const sigv4Ok = keyFormat !== "sigv4" || (keyLines.length > 0 && keyLines.every((l) => SIGV4_RE.test(l)));

  // 目标池:选模板 → 仅模板绑定的目标(交集);未选 → 全部可见目标。
  const poolTargets = useMemo<PoolTarget[]>(() => {
    if (!activeTemplate) return allTargets;
    const bound = new Set(activeTemplate.target_codes);
    return allTargets.filter((target) => bound.has(target.target_code));
  }, [allTargets, activeTemplate]);
  const relayPool = useMemo(() => poolTargets.filter((p) => p.site_type !== "push"), [poolTargets]);
  const pushPool = useMemo(() => poolTargets.filter((p) => p.site_type === "push"), [poolTargets]);
  const filteredRelay = useMemo(() => filterTargets(relayPool, filter), [relayPool, filter]);
  const filteredPush = useMemo(() => filterTargets(pushPool, filter), [pushPool, filter]);

  const selectedRelayCount = useMemo(
    () => Array.from(selected).filter((code) => relayPool.some((p) => p.target_code === code)).length,
    [selected, relayPool],
  );
  const selectedPushCount = useMemo(
    () => Array.from(selected).filter((code) => pushPool.some((p) => p.target_code === code)).length,
    [selected, pushPool],
  );

  function selectTemplate(id: number | null) {
    setTemplateId(id);
    setResult(null);
    if (id == null) {
      setSelected(new Set());
      return;
    }
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    setSelected(new Set(tpl.target_codes));
    if (tpl.models) setModels(tpl.models);
    if (tpl.model_mapping) setModelMapping(tpl.model_mapping);
  }

  function toggleTarget(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }
  function selectAll(list: PoolTarget[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      list.forEach((target) => (on ? next.add(target.target_code) : next.delete(target.target_code)));
      return next;
    });
  }

  const publishMut = useMutation({
    mutationFn: () =>
      supplierApi.publish({
        mode,
        name: name.trim(),
        keys: keyLines,
        base_url: baseUrl.trim() || undefined,
        template_id: templateId as number,
        models: models.trim() || undefined,
        model_mapping: modelMapping.trim() || undefined,
        target_codes: Array.from(selected),
      }),
    onSuccess: (r) => {
      setResult(r.success);
      setDuplicateKeys(r.keys
        .filter((key) => key.suspected_duplicate)
        .map((key) => ({ id: key.id, duplicateOf: key.duplicate_of_id ?? null })));
      setErr(null);
      const successMsg = t("distribute.successMsg", { created: r.created_keys, success: r.success_count });
      const autoAdded = r.auto_added_models ?? [];
      setMsg(
        autoAdded.length > 0
          ? `${successMsg} · ${t("distribute.autoAddedModels", { models: autoAdded.join(", ") })}`
          : successMsg,
      );
      if (mode === "single") setSingleKey("");
      else setMultiText("");
      qc.invalidateQueries({ queryKey: ["supplier", "keys"] });
      qc.invalidateQueries({ queryKey: ["supplier", "records"] });
      qc.invalidateQueries({ queryKey: ["supplier", "requests"] });
      qc.invalidateQueries({ queryKey: ["supplier", "dashboard"] });
    },
    onError: (e) => {
      setMsg(null);
      setErr(e instanceof ApiError ? e.message : t("distribute.submitFailed"));
    },
  });

  const canSubmit =
    templateId != null &&
    selected.size > 0 &&
    keyLines.length > 0 &&
    sigv4Ok &&
    (mode === "single" ? name.trim() !== "" : true) &&
    !publishMut.isPending;

  const submitLabel = !canSubmit && templateId == null
    ? t("distribute.needTemplate")
    : selected.size === 0
      ? t("distribute.selectTargetHint")
      : t("distribute.submit", {
          keyCount: keyLines.length,
          relayCount: selectedRelayCount,
          pushCount: selectedPushCount,
        });

  const modelOptions = useMemo(() => splitModels(models), [models]);

  const recent = recordsQ.data?.items ?? [];

  const renderPool = useCallback(
    (title: string, list: PoolTarget[], pool: PoolTarget[]) => (
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium">{title}</span>
          <span className="text-muted-foreground">{t("distribute.targetCount", { count: pool.length })}</span>
          <button
            type="button"
            className="ml-auto text-primary underline-offset-2 hover:underline"
            onClick={() => selectAll(pool, true)}
          >
            {t("distribute.selectAll")}
          </button>
          <button
            type="button"
            className="text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => selectAll(pool, false)}
          >
            {t("distribute.clearAll")}
          </button>
        </div>
        <ul className="max-h-48 space-y-0.5 overflow-y-auto">
          {list.map((target) => (
            <li key={target.target_code}>
              <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-accent">
                <input
                  type="checkbox"
                  checked={selected.has(target.target_code)}
                  onChange={() => toggleTarget(target.target_code)}
                />
                <span className="text-sm font-medium">{target.target_code}</span>
                <Badge variant={target.site_type === "push" ? "warning" : "default"}>
                  {target.site_type === "push" ? t("distribute.push") : t("distribute.relay")}
                </Badge>
              </label>
            </li>
          ))}
          {list.length === 0 && <li className="px-2 py-3 text-xs text-muted-foreground">—</li>}
        </ul>
      </div>
    ),
    [selected, t],
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Supplier / distribute</p>
        <h1 className="text-xl font-semibold">{t("distribute.title")}</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("distribute.description")}</p>
      </div>

      {(msg || err) && (
        <div
          className={`flex items-center rounded-md border px-3 py-2 text-sm ${
            err ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-green-200 bg-green-50 text-green-800"
          }`}
        >
          {err ?? msg}
          <button type="button" className="ml-2" onClick={() => { setMsg(null); setErr(null); }}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {duplicateKeys.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="font-medium">{t("distribute.duplicateWarning")}</div>
          <ul className="mt-1 list-disc pl-5 text-xs">
            {duplicateKeys.map((item) => (
              <li key={item.id}>
                {t("distribute.duplicateItem", { id: item.id, duplicateId: item.duplicateOf ?? "—" })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <section className={sectionCls}>
        <header className="border-b px-5 py-3">
          <h2 className="text-sm font-semibold">{t("distribute.stepTemplate")}</h2>
          <p className={`${hintCls} mt-0.5`}>{t("distribute.templateHint")}</p>
        </header>
        <div className="flex flex-wrap items-center gap-3 px-5 py-4">
          <select
            className={`${inpCls} max-w-md font-medium`}
            value={templateId ?? ""}
            onChange={(e) => selectTemplate(e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">{t("distribute.templatePlaceholder")}</option>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.code} · {tpl.name}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">{t("distribute.protocol")}</span>
            <span className="inline-flex h-9 min-w-[120px] items-center rounded-md border bg-muted/40 px-3 text-sm text-muted-foreground">
              {activeTemplate ? activeTemplate.protocol_type : t("distribute.protocolAuto")}
            </span>
          </div>
          {templates.length === 0 && <span className="text-xs text-muted-foreground">{t("distribute.noTemplates")}</span>}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_380px]">
        <div className="space-y-4">
          <section className={sectionCls}>
            <header className="border-b px-5 py-3">
              <h2 className="text-sm font-semibold">{t("distribute.stepInfo")}</h2>
            </header>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 px-5 py-4">
              <div className="col-span-2 space-y-1">
                <label className={labelCls}>
                  {t("distribute.name")} {mode === "single" && <span className="text-destructive">*</span>}
                </label>
                <input
                  className={inpCls}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={mode === "single" ? t("distribute.namePlaceholder") : t("distribute.namePlaceholderMulti")}
                  autoComplete="off"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <label className={labelCls}>{t("distribute.mode")}</label>
                <select
                  className={inpCls}
                  value={mode}
                  onChange={(e) => setMode(e.target.value as Mode)}
                >
                  <option value="single">{t("distribute.modeSingle")}</option>
                  <option value="multi">{t("distribute.modeMulti")}</option>
                  <option value="batch">{t("distribute.modeBatch")}</option>
                </select>
              </div>
            </div>
          </section>

          <section className={sectionCls}>
            <header className="flex items-center border-b px-5 py-3">
              <div>
                <h2 className="text-sm font-semibold">{t("distribute.stepKey")}</h2>
                <p className={`${hintCls} mt-0.5`}>
                  {mode === "single" ? t("distribute.keyHintSingle") : mode === "multi" ? t("distribute.keyHintMulti") : t("distribute.keyHintBatch")}
                </p>
              </div>
              {mode !== "single" && multiLines.length > 0 && (
                <button
                  type="button"
                  className="ml-auto inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-primary hover:bg-accent"
                  onClick={() => setMultiText(multiLines.join("\n"))}
                >
                  <RefreshCw className="h-3 w-3" /> {t("distribute.dedupe")}
                </button>
              )}
            </header>
            <div className="space-y-3 px-5 py-4">
              <div className="space-y-1">
                <label className={labelCls}>{t("distribute.keyFormat")}</label>
                <select
                  className={inpCls}
                  value={keyFormat}
                  onChange={(e) => setKeyFormat(e.target.value as KeyFormat)}
                >
                  <option value="bearer">Bearer (Authorization: Bearer &lt;key&gt;)</option>
                  <option value="x-api-key">x-api-key (header X-Api-Key: &lt;key&gt;)</option>
                  <option value="sigv4">AK|SK|Region (SigV4)</option>
                </select>
              </div>
              {mode === "single" ? (
                <div className="space-y-1">
                  <label className={labelCls}>
                    {t("distribute.keySingle")} <span className="text-destructive">*</span>
                  </label>
                  <input
                    className={inpCls}
                    type="text"
                    value={singleKey}
                    onChange={(e) => setSingleKey(e.target.value)}
                    placeholder={KEY_FORMAT_PLACEHOLDER[keyFormat].single}
                    autoComplete="new-password"
                    data-1p-ignore="true"
                    data-lpignore="true"
                  />
                </div>
              ) : (
                <div className="space-y-1">
                  <label className={labelCls}>{t("distribute.keyMulti")}</label>
                  <textarea
                    className="min-h-[160px] w-full rounded-lg border bg-background px-2.5 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-ring"
                    value={multiText}
                    onChange={(e) => setMultiText(e.target.value)}
                    placeholder={KEY_FORMAT_PLACEHOLDER[keyFormat].multi}
                  />
                  <p className={hintCls}>{t("distribute.parsedCount", { count: multiLines.length })}</p>
                </div>
              )}
              {!sigv4Ok && <p className="text-[11px] text-destructive">SigV4 需为 AK|SK|Region</p>}
              <div className="space-y-1">
                <label className={labelCls}>{t("distribute.baseUrl")}</label>
                <input
                  className={inpCls}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder={t("distribute.baseUrlPlaceholder")}
                />
              </div>
            </div>
          </section>

          <section className={sectionCls}>
            <header className="border-b px-5 py-3">
              <h2 className="text-sm font-semibold">{t("distribute.stepModels")}</h2>
            </header>
            <div className="space-y-3.5 px-5 py-4">
              <div className="space-y-1">
                <label className={labelCls}>{t("distribute.models")}</label>
                <input
                  className={`${inpCls} font-mono text-xs`}
                  value={models}
                  onChange={(e) => setModels(e.target.value)}
                  placeholder="gpt-4o,gpt-4o-mini"
                />
              </div>
              <div className="space-y-1">
                <label className={labelCls}>{t("distribute.modelMapping")}</label>
                <ModelMappingEditor
                  value={modelMapping}
                  onChange={setModelMapping}
                  sourceModelOptions={modelOptions}
                  targetModelOptions={modelOptions}
                />
              </div>
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="sticky top-4 space-y-4">
            <section className={sectionCls}>
              <header className="border-b px-4 py-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold">{t("distribute.stepTargets")}</h2>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    {t("distribute.selected", { count: selected.size })}
                  </span>
                </div>
                <input
                  className="mt-2 h-8 w-full rounded-md border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder={t("distribute.filterPlaceholder")}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </header>
              <div className="space-y-3 px-4 py-3">
                {renderPool(t("distribute.relay"), filteredRelay, relayPool)}
                <div className="border-t" />
                {renderPool(t("distribute.push"), filteredPush, pushPool)}
              </div>
            </section>

            <section className={sectionCls}>
              <div className="space-y-2 px-4 py-3">
                <Button
                  className="w-full"
                  disabled={!canSubmit}
                  onClick={() => {
                    const confirmText = t("distribute.confirm", { keyCount: keyLines.length, targetCount: selected.size });
                    if (window.confirm(confirmText)) publishMut.mutate();
                  }}
                >
                  <Rocket className="mr-1 h-4 w-4" />
                  {publishMut.isPending ? t("distribute.submitting") : submitLabel}
                </Button>
                {keyLines.length === 0 && <p className={hintCls}>{t("distribute.noKeys")}</p>}
              </div>
            </section>
          </div>
        </aside>
      </div>

      {result && (
        <section className={sectionCls}>
          <header className="border-b px-5 py-3">
            <h2 className="text-sm font-semibold">{t("distribute.result")}</h2>
          </header>
          <ul className="space-y-1 p-4">
            {result.map((entry) => (
              <li key={entry.id} className="flex items-center gap-2 text-sm">
                <Badge variant={STATUS_VARIANT[entry.status]}>{entry.status}</Badge>
                <span className="font-medium">#{entry.key_id}</span>
                <span className="text-muted-foreground">→ {entry.target_code}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={sectionCls}>
        <header className="border-b px-5 py-3">
          <h2 className="text-sm font-semibold">{t("distribute.recent")}</h2>
        </header>
        <ul className="divide-y">
          {recent.map((entry) => (
            <li key={entry.id} className="flex items-center gap-2 px-5 py-2 text-sm">
              <Badge variant={STATUS_VARIANT[entry.status]}>{entry.status}</Badge>
              <span className="font-medium">#{entry.key_id}</span>
              <span className="text-muted-foreground">→ {entry.target_code}</span>
            </li>
          ))}
          {recent.length === 0 && (
            <li className="px-5 py-4 text-sm text-muted-foreground">
              {recordsQ.isLoading ? "…" : t("distribute.recentEmpty")}
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}

function filterTargets(list: PoolTarget[], filter: string): PoolTarget[] {
  const q = filter.trim().toLowerCase();
  if (!q) return list;
  return list.filter((target) => target.target_code.toLowerCase().includes(q));
}
